import { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, X, RotateCcw, Filter, Download } from 'lucide-react';
import { api } from '../lib/api.jsx';
import { useTableSort } from '../lib/useTableSort.jsx';
import { fmtMachineType, fmtOnlineStatus, onlineSortValue } from '../lib/format.jsx';
import { MachineTypeBadge } from '../components/MachineTypeBadge.jsx';
import { useDashboard } from './dashboardContext.jsx';
import { ClientPicker } from '../components/ClientPicker.jsx';

const inputCls =
  'w-full bg-sidebar border border-rule rounded-lg px-3 py-2 text-fg-2 text-xs outline-none focus:border-blue-500 box-border';
const labelCls = 'text-[11px] uppercase tracking-wider text-muted-2 font-semibold mb-1.5 block';
const sectionCls = 'mb-4 pb-4 border-b border-rule';

const fmtMB = (mb) => (mb >= 1024 ? (mb / 1024).toFixed(1) + ' Go' : mb + ' Mo');

// Même convention que la jauge CPU/RAM/disque du Dashboard (rouge >=95%, ambre >=80%).
const PBar = (p) => (
  <div className="w-full h-2 bg-rule rounded overflow-hidden">
    <div
      className="h-full rounded"
      style={{
        width: `${Math.min(p, 100)}%`,
        background: p >= 95 ? '#ef4444' : p >= 80 ? '#f59e0b' : '#22c55e',
        transition: 'width .5s',
      }}
    />
  </div>
);
const barCell = (p) => (
  <div className="flex items-center gap-1.5" style={{ minWidth: 90 }}>
    <div className="flex-1">{PBar(p || 0)}</div>
    <span className="text-[11px] text-muted font-mono text-right" style={{ minWidth: 32 }}>
      {(p || 0).toFixed(0)}%
    </span>
  </div>
);
const alertBadge = (n) =>
  n > 0 ? (
    <span
      className="px-2 py-0.5 rounded text-[10px] font-semibold"
      style={{
        color: n >= 3 ? '#fca5a5' : '#fcd34d',
        background: n >= 3 ? 'rgba(239,68,68,.12)' : 'rgba(245,158,11,.12)',
      }}
    >
      {n}
    </span>
  ) : (
    <span className="text-muted-3 text-[10px]">—</span>
  );

const ChipToggle = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`px-2.5 py-1 rounded-md border text-[11px] cursor-pointer transition-colors ${
      active
        ? 'border-blue-500 bg-blue-500/10 text-blue-400 font-semibold'
        : 'border-rule bg-transparent text-muted hover:bg-card-hi hover:text-fg-2'
    }`}
  >
    {label}
  </button>
);

const initialFilter = {
  os: [],
  platform: [],
  osArchitecture: [],
  machineType: [],
  clientIds: [],
  isOnline: '', // '', 'true', 'false'
  minRamMB: '',
  maxRamMB: '',
  minPendingUpdates: '',
  hasSoftware: '',
  missingSoftware: '',
  hasPendingUpdate: '',
  domain: '',
  search: '',
};

export function SearchPage() {
  const { navigate } = useDashboard();
  const [filter, setFilter] = useState(initialFilter);
  const [facets, setFacets] = useState({
    osFamilies: [],
    osNames: [],
    platforms: [],
    machineTypes: [],
    architectures: [],
    domains: [],
  });
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const PAGE_SIZE = 100;

  const { sort, sortRows, headerProps, arrow } = useTableSort('computerName', 'asc');

  // Charge facets une fois (les clients sont chargés à la demande par ClientPicker)
  useEffect(() => {
    api.get('/devices/search/facets').then((r) => r && setFacets(r));
  }, []);

  const toggleArr = (field, value) => {
    setFilter((f) => {
      const cur = f[field] || [];
      const next = cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value];
      return { ...f, [field]: next };
    });
    setPage(1);
  };

  const clearFilters = () => {
    setFilter(initialFilter);
    setPage(1);
  };

  // Recherche : envoie le filtre tel quel (le backend ignore les listes vides)
  const buildBody = useCallback(
    (overridePage) => {
      const b = {
        os: filter.os.length ? filter.os : null,
        osArchitecture: filter.osArchitecture.length ? filter.osArchitecture : null,
        machineType: filter.machineType.length ? filter.machineType : null,
        platform: filter.platform.length ? filter.platform : null,
        clientIds: filter.clientIds.length ? filter.clientIds : null,
        isOnline: filter.isOnline === '' ? null : filter.isOnline === 'true',
        minRamMB: filter.minRamMB ? parseInt(filter.minRamMB) * 1024 : null, // user saisit en Go → convertir Mo
        maxRamMB: filter.maxRamMB ? parseInt(filter.maxRamMB) * 1024 : null,
        minPendingUpdates: filter.minPendingUpdates ? parseInt(filter.minPendingUpdates) : null,
        hasSoftware: filter.hasSoftware || null,
        missingSoftware: filter.missingSoftware || null,
        hasPendingUpdate: filter.hasPendingUpdate || null,
        domain: filter.domain || null,
        search: filter.search || null,
        page: overridePage ?? page,
        pageSize: PAGE_SIZE,
        sortKey: sort.key,
        sortDir: sort.dir,
      };
      return b;
    },
    [filter, page, sort]
  );

  const doSearch = useCallback(async () => {
    setLoading(true);
    const r = await api.post('/devices/search', buildBody());
    if (r) {
      setResults(r.devices || []);
      setTotalCount(r.totalCount || 0);
    }
    setLoading(false);
  }, [buildBody]);

  useEffect(() => {
    doSearch();
  }, [doSearch]);

  const exportCsv = async () => {
    // Récupère l'ensemble en une seule passe (pageSize=500 max côté backend)
    const r = await api.post('/devices/search', { ...buildBody(1), pageSize: 500 });
    const rows = r?.devices || [];
    const header = [
      'computerName',
      'label',
      'machineType',
      'osName',
      'osVersion',
      'osArchitecture',
      'totalMemoryGB',
      'pendingUpdates',
      'clientName',
      'siteName',
      'isOnline',
      'ipAddress',
      'agentVersion',
    ];
    const esc = (v) => {
      const s = v == null ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? '"' + s.replaceAll('"', '""') + '"'
        : s;
    };
    const lines = [header.join(',')];
    for (const d of rows) {
      lines.push(
        [
          d.computerName,
          d.label,
          fmtMachineType(d.machineType),
          d.osName,
          d.osVersion,
          d.osArchitecture,
          d.totalMemoryMB ? (d.totalMemoryMB / 1024).toFixed(1) : '',
          d.pendingUpdatesCount,
          d.clientName,
          d.siteName,
          d.isOnline,
          d.ipAddress,
          d.agentVersion,
        ]
          .map(esc)
          .join(',')
      );
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `recherche-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const filterCount = useMemo(() => {
    let n = 0;
    n += filter.os.length;
    n += filter.osArchitecture.length;
    n += filter.machineType.length;
    n += filter.platform.length;
    n += filter.clientIds.length;
    if (filter.isOnline !== '') n++;
    if (filter.minRamMB) n++;
    if (filter.maxRamMB) n++;
    if (filter.minPendingUpdates) n++;
    if (filter.hasSoftware) n++;
    if (filter.missingSoftware) n++;
    if (filter.hasPendingUpdate) n++;
    if (filter.domain) n++;
    if (filter.search) n++;
    return n;
  }, [filter]);

  // Tri client-side appliqué sur la page courante (le backend a déjà appliqué un
  // tri global ; ici on permet de re-trier en local pour les colonnes calculées)
  const sortedRows = sortRows(results, (d, key) => {
    if (key === 'totalMemoryMB') return d.totalMemoryMB || 0;
    if (key === 'pendingUpdatesCount') return d.pendingUpdatesCount || 0;
    if (key === 'alertCount') return d.alertCount || 0;
    if (key === 'cpuPercent') return d.cpuPercent || 0;
    if (key === 'memoryPercent') return d.memoryPercent || 0;
    if (key === 'diskPercent') return d.diskPercent || 0;
    if (key === 'isOnline') return onlineSortValue(d.isOnline, d.lastHeartbeat);
    if (key === 'machineType') return fmtMachineType(d.machineType);
    if (key === 'lastHeartbeat') return d.lastHeartbeat ? new Date(d.lastHeartbeat) : null;
    return d[key];
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h1 className="text-[22px] font-bold text-fg flex items-center gap-2">
          <Search size={20} strokeWidth={1.75} className="text-blue-400" /> Recherche avancée
        </h1>
        <div className="flex gap-2">
          <button
            onClick={exportCsv}
            disabled={totalCount === 0}
            className="bg-blue-500 border-none rounded-lg px-3.5 py-2 text-white text-xs font-semibold cursor-pointer hover:bg-blue-600 inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-default whitespace-nowrap"
          >
            <Download size={14} strokeWidth={1.75} /> Exporter CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 items-start">
        {/* ─── Panneau de filtres ─── */}
        <div className="bg-card border border-rule rounded-2xl p-5 sticky top-4 max-h-[calc(100vh-120px)] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-fg-2 text-xs font-bold uppercase tracking-wider">
              <Filter size={13} strokeWidth={1.75} /> Filtres
              {filterCount > 0 && (
                <span className="bg-blue-500/20 text-blue-400 rounded-full px-2 py-0.5 text-[10px]">
                  {filterCount}
                </span>
              )}
            </div>
            {filterCount > 0 && (
              <button
                onClick={clearFilters}
                className="bg-transparent border border-rule-3 rounded px-2 py-0.5 text-muted text-[10px] cursor-pointer hover:bg-rule inline-flex items-center gap-1"
                title="Réinitialiser tous les filtres"
              >
                <RotateCcw size={11} strokeWidth={1.75} /> Reset
              </button>
            )}
          </div>

          {/* Recherche libre */}
          <div className={sectionCls}>
            <label className={labelCls}>Recherche libre</label>
            <input
              placeholder="Nom, IP, label, OS…"
              value={filter.search}
              onChange={(e) => {
                setFilter({ ...filter, search: e.target.value });
                setPage(1);
              }}
              className={inputCls}
            />
          </div>

          {/* Plateforme — filtre le plus large, avant la famille d'OS */}
          {facets.platforms?.length > 1 && (
            <div className={sectionCls}>
              <label className={labelCls}>Plateforme</label>
              <div className="flex flex-wrap gap-1.5">
                {facets.platforms.map((p) => (
                  <ChipToggle
                    key={p}
                    label={p === 'linux' ? 'Linux' : p === 'routeros' ? 'RouterOS' : 'Windows'}
                    active={filter.platform.includes(p)}
                    onClick={() => toggleArr('platform', p)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Familles OS */}
          {facets.osFamilies?.length > 0 && (
            <div className={sectionCls}>
              <label className={labelCls}>Système d'exploitation</label>
              <div className="flex flex-wrap gap-1.5">
                {facets.osFamilies.map((f) => (
                  <ChipToggle
                    key={f}
                    label={f}
                    active={filter.os.includes(f)}
                    onClick={() => toggleArr('os', f)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Architecture */}
          {facets.architectures?.length > 0 && (
            <div className={sectionCls}>
              <label className={labelCls}>Architecture</label>
              <div className="flex flex-wrap gap-1.5">
                {facets.architectures.map((a) => (
                  <ChipToggle
                    key={a}
                    label={a}
                    active={filter.osArchitecture.includes(a)}
                    onClick={() => toggleArr('osArchitecture', a)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Type machine */}
          {facets.machineTypes?.length > 0 && (
            <div className={sectionCls}>
              <label className={labelCls}>Type</label>
              <div className="flex flex-wrap gap-1.5">
                {facets.machineTypes.map((t) => (
                  <ChipToggle
                    key={t}
                    /* La valeur envoyée au serveur reste la valeur brute
                       (« Server ») : seule l'étiquette est traduite. */
                    label={fmtMachineType(t)}
                    active={filter.machineType.includes(t)}
                    onClick={() => toggleArr('machineType', t)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Statut */}
          <div className={sectionCls}>
            <label className={labelCls}>Statut</label>
            <div className="flex gap-1.5">
              <ChipToggle
                label="Tous"
                active={filter.isOnline === ''}
                onClick={() => {
                  setFilter({ ...filter, isOnline: '' });
                  setPage(1);
                }}
              />
              <ChipToggle
                label="En ligne"
                active={filter.isOnline === 'true'}
                onClick={() => {
                  setFilter({ ...filter, isOnline: 'true' });
                  setPage(1);
                }}
              />
              <ChipToggle
                label="Hors ligne"
                active={filter.isOnline === 'false'}
                onClick={() => {
                  setFilter({ ...filter, isOnline: 'false' });
                  setPage(1);
                }}
              />
            </div>
          </div>

          {/* RAM */}
          <div className={sectionCls}>
            <label className={labelCls}>RAM (Go)</label>
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                placeholder="min"
                value={filter.minRamMB}
                onChange={(e) => {
                  setFilter({ ...filter, minRamMB: e.target.value });
                  setPage(1);
                }}
                className={inputCls}
              />
              <span className="text-muted-2 text-xs">→</span>
              <input
                type="number"
                placeholder="max"
                value={filter.maxRamMB}
                onChange={(e) => {
                  setFilter({ ...filter, maxRamMB: e.target.value });
                  setPage(1);
                }}
                className={inputCls}
              />
            </div>
          </div>

          {/* Mises à jour en attente */}
          <div className={sectionCls}>
            <label className={labelCls}>MAJ Windows en attente</label>
            <input
              type="number"
              placeholder="Au moins X"
              value={filter.minPendingUpdates}
              onChange={(e) => {
                setFilter({ ...filter, minPendingUpdates: e.target.value });
                setPage(1);
              }}
              className={inputCls}
            />
            <input
              placeholder="KB précis ou substring titre"
              value={filter.hasPendingUpdate}
              onChange={(e) => {
                setFilter({ ...filter, hasPendingUpdate: e.target.value });
                setPage(1);
              }}
              className={`${inputCls} mt-1.5`}
            />
          </div>

          {/* Logiciels */}
          <div className={sectionCls}>
            <label className={labelCls}>Logiciel installé</label>
            <input
              placeholder="ex: Chrome, Office@2019"
              value={filter.hasSoftware}
              onChange={(e) => {
                setFilter({ ...filter, hasSoftware: e.target.value });
                setPage(1);
              }}
              className={inputCls}
            />
            <label className={`${labelCls} mt-3`}>Logiciel manquant</label>
            <input
              placeholder="ex: 7-Zip"
              value={filter.missingSoftware}
              onChange={(e) => {
                setFilter({ ...filter, missingSoftware: e.target.value });
                setPage(1);
              }}
              className={inputCls}
            />
          </div>

          {/* Client */}
          <div className={sectionCls}>
            <label className={labelCls}>Client</label>
            <ClientPicker
              multiple
              value={filter.clientIds}
              onChange={(ids) => {
                setFilter({ ...filter, clientIds: ids });
                setPage(1);
              }}
              placeholder="Ajouter des clients…"
            />
          </div>

          {/* Domaine */}
          {facets.domains?.length > 0 && (
            <div className="mb-1">
              <label className={labelCls}>Domaine / Workgroup</label>
              <select
                value={filter.domain}
                onChange={(e) => {
                  setFilter({ ...filter, domain: e.target.value });
                  setPage(1);
                }}
                className={`${inputCls} cursor-pointer`}
              >
                <option value="">Tous</option>
                {facets.domains.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* ─── Résultats ─── */}
        <div>
          <div className="text-[11px] text-muted-2 mb-2.5 flex items-center justify-between">
            <span>
              {loading
                ? 'Recherche…'
                : `${totalCount.toLocaleString('fr-FR')} appareil${totalCount !== 1 ? 's' : ''} trouvé${totalCount !== 1 ? 's' : ''}` +
                  (totalPages > 1 ? ` • Page ${page}/${totalPages}` : '')}
            </span>
          </div>

          <div className="bg-card border border-rule rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead className="bg-sidebar">
                  <tr>
                    {[
                      { key: 'computerName', label: 'Nom' },
                      { key: 'machineType', label: 'Type' },
                      { key: 'osName', label: 'OS' },
                      { key: 'osArchitecture', label: 'Arch' },
                      { key: 'totalMemoryMB', label: 'RAM' },
                      { key: 'pendingUpdatesCount', label: 'MAJ' },
                      { key: 'alertCount', label: 'Alertes' },
                      { key: 'cpuPercent', label: 'CPU' },
                      { key: 'memoryPercent', label: 'Mém.' },
                      { key: 'diskPercent', label: 'Disque' },
                      { key: 'clientName', label: 'Client' },
                      { key: 'siteName', label: 'Site' },
                      { key: 'isOnline', label: 'Statut' },
                    ].map((c) => (
                      <th
                        key={c.key}
                        {...headerProps(c.key)}
                        className="px-3 py-2.5 text-left text-muted-2 font-medium text-[11px] uppercase border-b border-rule whitespace-nowrap"
                      >
                        {c.label}
                        {arrow(c.key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.length === 0 ? (
                    <tr>
                      <td colSpan={13} className="px-3 py-10 text-center text-muted-2 text-xs">
                        {loading
                          ? 'Recherche…'
                          : filterCount > 0
                            ? 'Aucun appareil ne correspond aux filtres'
                            : 'Aucun appareil'}
                      </td>
                    </tr>
                  ) : (
                    sortedRows.map((d) => (
                      <tr
                        key={d.id}
                        onClick={() => navigate('deviceDetail', { device: d.id })}
                        className="border-b border-sidebar cursor-pointer transition-colors hover:bg-blue-500/5"
                      >
                        <td className="px-3 py-2 text-fg-2 font-mono">
                          <div className="font-semibold text-fg-2">{d.label || d.computerName}</div>
                          {d.label && (
                            <div className="text-[10px] text-muted-2 mt-0.5">{d.computerName}</div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <MachineTypeBadge machineType={d.machineType} platform={d.platform} />
                        </td>
                        <td className="px-3 py-2 text-fg-2">
                          <div>{d.osName}</div>
                          {d.osVersion && (
                            <div className="text-[10px] text-muted-2 mt-0.5 font-mono">{d.osVersion}</div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {d.osArchitecture ? (
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-mono ${
                                (d.osArchitecture || '').toLowerCase().includes('arm')
                                  ? 'bg-orange-500/15 text-orange-400'
                                  : 'bg-blue-500/15 text-blue-400'
                              }`}
                            >
                              {d.osArchitecture}
                            </span>
                          ) : (
                            <span className="text-muted-3 text-[10px]">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-fg-2 font-mono text-[11px]">
                          {d.totalMemoryMB ? fmtMB(d.totalMemoryMB) : '—'}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`text-[11px] font-mono font-bold ${
                              (d.pendingUpdatesCount || 0) > 10
                                ? 'text-red-300'
                                : (d.pendingUpdatesCount || 0) > 0
                                  ? 'text-amber-300'
                                  : 'text-emerald-300'
                            }`}
                          >
                            {d.pendingUpdatesCount ?? 0}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">{alertBadge(d.alertCount)}</td>
                        <td className="px-3 py-2">{barCell(d.cpuPercent)}</td>
                        <td className="px-3 py-2">{barCell(d.memoryPercent)}</td>
                        <td className="px-3 py-2">{barCell(d.diskPercent)}</td>
                        <td className="px-3 py-2 text-muted text-[11.5px]">{d.clientName || '—'}</td>
                        <td className="px-3 py-2 text-muted text-[11.5px]">{d.siteName || '—'}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <div className={`w-1.5 h-1.5 rounded-full ${d.isOnline ? 'bg-ok' : 'bg-crit'}`} />
                            <span
                              className={`text-[11px] ${d.isOnline ? 'text-emerald-300' : 'text-red-300'}`}
                              title={!d.isOnline && d.lastHeartbeat ? `Dernier heartbeat : ${new Date(d.lastHeartbeat).toLocaleString('fr-FR')}` : undefined}
                            >
                              {fmtOnlineStatus(d.isOnline, d.lastHeartbeat)}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-1.5 mt-4">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className={`bg-rule border-none rounded-md px-3.5 py-1.5 text-xs font-semibold ${
                  page === 1 ? 'text-muted-3 cursor-default' : 'text-fg-2 cursor-pointer hover:bg-rule-2'
                }`}
              >
                ← Précédent
              </button>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let p = i + 1;
                if (totalPages > 7) {
                  if (page < 5) p = i + 1;
                  else if (page > totalPages - 4) p = totalPages - 6 + i;
                  else p = page - 3 + i;
                }
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`border-none rounded-md px-2.5 py-1.5 text-xs font-semibold min-w-[32px] cursor-pointer ${
                      p === page ? 'bg-blue-500 text-white' : 'bg-rule text-muted hover:bg-rule-2'
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className={`bg-rule border-none rounded-md px-3.5 py-1.5 text-xs font-semibold ${
                  page >= totalPages
                    ? 'text-muted-3 cursor-default'
                    : 'text-fg-2 cursor-pointer hover:bg-rule-2'
                }`}
              >
                Suivant →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
